import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getCookieByName } from '@99designs/browser';
import { Box } from '@99designs/emu';
import {
    ProductDetailsContainer,
    getDesignInspiration,
    getPageConfigFromContentful,
    getProductGroupConfigByKey,
    getProductsByProductGroupName,
    getRelatedProductsByProductGroupKey,
    isValidProductGroupKey,
} from '@99designs/feature-product-details';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { createSSRApolloClient } from '@99designs/graph-utils';
import { Tenant } from '@99designs/marketing-ui';
import { Site } from '@99designs/site-navigation/lib/Site';
import DefaultPageLayout from '../../../components/common/DefaultPageLayout';
import { Head } from '../../../components/common/Head/';
import { getContentfulClient } from '../../../lib/getContentfulClient';
import { getDefaultPageProps } from '../../../lib/getDefaultPageProps';
import type { Design } from '@99designs/feature-design-inspiration';
import type {
    ContestProduct,
    FixedPriceProjectProduct,
    ProductDetailsInspirationQuery,
    ProductGroup,
    ProductGroupKey,
    ProductGroupName,
    RelatedProduct,
} from '@99designs/feature-product-details';
import type { CurrencyCode } from '@99designs/i18n';
import type { ZendeskLanguage } from '@99designs/routing';
import type { Role } from '@99designs/site-navigation/consts';
import type { SiteConfig } from '@99designs/site-navigation/lib/Site';

function ProductDetailsPage({
    siteConfig,
    products,
    relatedProducts,
    designInspiration,
    userRole,
    productGroupKey,
    productGroup,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);
    const currency = site.siteConfig['currency'] as CurrencyCode;

    return (
        <DefaultPageLayout role={userRole} site={site}>
            <Head
                title={formatMessage(__('{productGroupTitle} details'), {
                    productGroupTitle: productGroup.attributes.title || products[0].title || '',
                })}
                description={productGroup.attributes.description || products[0].description || ''}
                robots="noindex"
                currentDomain={site.getDomain()}
            >
                <meta property="og:image" content={productGroup.attributes.hero[0].src} />
            </Head>
            <Box paddingTop={['2bu', '3bu', '3bu', '3bu']}>
                <ProductDetailsContainer
                    productGroup={productGroup}
                    productGroupKey={productGroupKey}
                    products={products}
                    designInspiration={designInspiration}
                    relatedProducts={relatedProducts}
                    currency={currency}
                    lang={site.getLanguageCode() as ZendeskLanguage}
                    tenant={Tenant.NinetyNineDesigns}
                />
            </Box>
        </DefaultPageLayout>
    );
}

type ServerSideProps = {
    products: ContestProduct[] | FixedPriceProjectProduct[];
    relatedProducts: RelatedProduct[] | null; // specifying this in order to enforce null checking
    designInspiration: Design[] | null; // specifying this in order to enforce null checking
    productGroupKey: ProductGroupKey;
    siteConfig: string;
    userRole: Role;
    productGroup: ProductGroup;
};

// TODO: Getting around type checking to address some noisy monitors:
// https://vistaprint.slack.com/archives/C041ZRTUQR2/p1672171082383019
// Clean up once we are back from the xmas break.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ServerSidePropsWithError = Record<string, any>;

export const getServerSideProps: GetServerSideProps<
    ServerSideProps | ServerSidePropsWithError
> = async (context) => {
    const { req, res, query } = context;
    const { urlSlug } = query;
    const contentfulLanguage = context.locale?.slice(0, 2) || 'en';

    if (process.env.USE_CSRF_CHECK) {
        const csrfToken = getCookieByName('_99csrf', req.headers['cookie'] || '');
        if (!csrfToken) {
            res.statusCode = 401;
            res.end();
            return {
                props: {},
            };
        }
    }

    if (!isValidProductGroupKey(urlSlug as string)) {
        return {
            notFound: true,
        };
    }

    const productGroupConfig = getProductGroupConfigByKey(urlSlug as ProductGroupKey);

    const client = createSSRApolloClient(req);
    const contentfulClient = getContentfulClient();

    const [defaultPageProps, productGroupAttributes, products, relatedProducts, designInspiration] =
        await Promise.all([
            getDefaultPageProps(context),
            getPageConfigFromContentful(
                productGroupConfig.name as ProductGroupName,
                contentfulClient,
                contentfulLanguage
            ),
            // Call GraphQL here in order to make SSR work
            getProductsByProductGroupName(client, productGroupConfig.name as ProductGroupName),
            getRelatedProductsByProductGroupKey(client, urlSlug as ProductGroupKey),
            getDesignInspiration(client, {
                categories: [productGroupConfig.designInspirationCategoryKey],
                designerLevels:
                    urlSlug === 'logo-essential' || urlSlug === 'logo-essential-and-consultation'
                        ? ['ENTRY']
                        : [],
            } as ProductDetailsInspirationQuery),
        ]);

    const productGroup = {
        ...productGroupConfig,
        ...productGroupAttributes,
    };

    if (!productGroup.attributes.active && query.preview !== 'true') {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            ...defaultPageProps,
            products,
            relatedProducts,
            designInspiration,
            productGroupKey: urlSlug as ProductGroupKey,
            productGroup,
        },
    };
};

export default ProductDetailsPage;
